// @ts-strict-ignore
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Arrange, Separator, Button, Radio, Pill, EmptyState, useToast } from "@aviary";
import { useAdminCartCreateMutation } from "@emerson/data/mutations/AdminCartCreateMutation/AdminCartCreate.mutation";
import { useAdminCartSelectMutation } from "@emerson/data/mutations/AdminCartSelectMutation/AdminCartSelect.mutation";
import { l } from "@emerson/locales/i18n";
import type { InternalWholesaleCartsQueryData } from "@shared/data/queries/InternalWholesaleCartsQuery/InternalWholesaleCarts.query";
import { useWholesaleCartQuery } from "@shared/data/queries/WholesaleCartQuery/WholesaleCart.query";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import * as styles from "./CartBody.styles";

interface Props {
  internalWholesaleCartsData: InternalWholesaleCartsQueryData;
  setCartToRemove: (orderId: string) => void;
}

const CartBody = ({ internalWholesaleCartsData, setCartToRemove }: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();

  const carts = internalWholesaleCartsData?.currentPractitioner?.internalWholesaleCarts;
  const [selectedCartId, setSelectedCartId] = useState<string>(null);

  const { data } = useWholesaleCartQuery();
  const [createAdminCart, { loading: cartCreationLoading }] = useAdminCartCreateMutation({
    onCompleted: createAdminCartData =>
      handleCartCreationCompleted(createAdminCartData.wholesaleCartAdminCreate),
    refetchQueries: ["WholesaleCart_Shared_Query", "InternalWholesaleCarts_Shared_Query"],
  });

  const [selectInternalCart, { loading: cartSelectionLoading }] = useAdminCartSelectMutation({
    onCompleted: () => {
      makeToast("success", t(l.cart.CartSelected));
    },
    refetchQueries: ["WholesaleCart_Shared_Query"],
  });

  const currentCartId = data?.currentOffice?.wholesaleCart?.id;

  useEffect(() => {
    if (currentCartId) {
      setSelectedCartId(currentCartId);
    }
  }, [currentCartId]);

  const handleCartCreationCompleted = cartCreateData => {
    const message = cartCreateData?.errors?.message;
    if (message) makeToast("error", message);
  };

  const handleNewCart = () => {
    createAdminCart();
  };

  const getNumberOfCartItems = lineItems => {
    return lineItems.map(item => item.quantity).reduce((sum: number, value) => sum + value, 0);
  };

  const renderCartItemsCount = lineItems => {
    const quantity = getNumberOfCartItems(lineItems);

    return (
      <Pill size="xsmall" color="system">
        {t(l.cart.NumItems, { quantity, count: quantity })}
      </Pill>
    );
  };

  const handleConfirm = () => {
    selectInternalCart({ variables: { input: { orderId: selectedCartId } } });
  };

  const newCart = (
    <Button size="xsmall" intention="system" onClick={handleNewCart} disabled={cartCreationLoading}>
      {t(l.cart.NewCart)}
    </Button>
  );

  const confirm = carts?.length && (
    <Button size="xsmall" onClick={handleConfirm} disabled={cartSelectionLoading}>
      {t(l.common.Confirm)}
    </Button>
  );

  const renderCarts = () => {
    if (!carts?.length) {
      return (
        <Arrange isVertical spacing="xxsmall" justify="center" isFullWidth>
          <EmptyState
            css={styles.emptyWrapper}
            illustration="emptyBottle"
            header={t(l.cart.NoIternalCarts)}
            isBorderless
          />
        </Arrange>
      );
    }

    return (
      <>
        <Typography type="h5" css={styles.centeredText}>
          {t(l.cart.EditCart)}
        </Typography>

        <Arrange isVertical isFullWidth>
          {carts?.map(({ id, isCreatedByPractitioner, _legacyId, lineItems }) => (
            <Radio
              key={id}
              name="order_id"
              checked={selectedCartId === id}
              onChange={() => setSelectedCartId(id)}
              value={id}
            >
              <Arrange isWrapped={false} justify="spaceBetween" alignment="center">
                <Arrange alignment="center" spacing="xxsmall" isWrapped={false}>
                  <Typography type="body">
                    {_legacyId}{" "}
                    {!!isCreatedByPractitioner && <span>{t(l.cart.Practitioners)}</span>}
                  </Typography>
                  {renderCartItemsCount(lineItems)}
                </Arrange>
                {!isCreatedByPractitioner && (
                  <Button
                    intention="danger"
                    isIcon
                    onClick={() => setCartToRemove(id)}
                    aria-label="delete-button"
                    size="xsmall"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                )}
              </Arrange>
            </Radio>
          ))}
        </Arrange>
      </>
    );
  };

  return (
    <Arrange isWrapped={false} isVertical spacing="xsmall" isFullWidth>
      {renderCarts()}

      <Separator css={styles.separator} />
      <Arrange isWrapped={false} spacing="xsmall" justify="end">
        {newCart}
        {confirm}
      </Arrange>
    </Arrange>
  );
};

export { CartBody };
